// =============================================================================
// Blocchi del payload delega F24: i TIPI del wire CGN e le LISTE dei loro campi,
// nello stesso file per costruzione.
//
// Perché insieme
// --------------
// Le liste sono lo scheletro della griglia di revisione (il payload di risposta
// porta solo i campi che l'estrattore ha letto dal cartaceo: senza scheletro un
// campo obbligatorio ma assente dal modulo non sarebbe compilabile). Devono
// coprire TUTTI i campi dell'interfaccia corrispondente, e il modo per garantirlo
// è vincolare l'una all'altra sulla DICHIARAZIONE dell'array — `arrayOfAll` qui
// sotto. Un campo aggiunto all'interfaccia e non elencato non compila: non c'è
// una finestra in cui le due metà possono divergere.
//
// Il muro tipo/valore di TypeScript
// ---------------------------------
// Un'interfaccia è un TIPO (non esiste a runtime), una lista di nomi è un VALORE.
// `keyof` attraversa il muro in una sola direzione — da tipo a tipo — quindi il
// vincolo si può scrivere solo sul valore, e per scriverlo il tipo deve essere
// raggiungibile dallo stesso file. Finché interfacce e liste vivevano in package
// diversi (contratto HTTP in `apps/api`, scheletro condiviso in `@repo/utils`)
// serviva un terzo file di sole asserzioni, che si poteva dimenticare di
// aggiornare. Incollandole qui il terzo file non serve più.
//
// Perché solo questi tipi
// -----------------------
// Qui stanno SOLO i blocchi che hanno una lista di campi da vincolare — anagrafica,
// totali di sezione, righe tributo. Gli envelope di richiesta/risposta
// (`DelegaF24*V2`, `GenericDelegaF24`, `Info*` con la diagnostica per campo,
// token, notifiche webhook) restano in `apps/api/src/cgn-fintech/client/cgn-f24.types.ts`:
// sono il contratto HTTP, vanno letti accanto a `docs/specs/cgn-f24-openapi.json`,
// non hanno liste da vincolare e il frontend non li attraversa. Il client li
// riesporta, quindi per i suoi consumer l'import è invariato.
//
// Naming: `snake_case` del wire. Questi tipi SONO il contratto HTTP; i DTO
// Previxio (camelCase) vivono altrove.
// =============================================================================
// -----------------------------------------------------------------------------
// Vincolo di esaustività
// -----------------------------------------------------------------------------
/**
 * Dichiara un array che deve elencare TUTTI i valori di `T`.
 *
 * Come si legge l'errore: se manca un campo, il compilatore confronta il primo
 * elemento dell'array con la sentinella e stampa
 * `Type '"<primo campo>"' is not assignable to type '"CAMPO MANCANTE: <campo>"'`
 * — il nome da aggiungere è quello dentro la sentinella (più di uno se ne
 * mancano diversi). Se invece un nome NON è un campo di `T`, l'errore è sul
 * nome di troppo: `Type '"<nome>"' is not assignable to type '<campi ammessi>'`.
 */
const arrayOfAll = () => (array) => array;
// -----------------------------------------------------------------------------
// Campi dei tre modelli
//
// Ogni lista copre TUTTI i campi della sua interfaccia (vincolo `arrayOfAll`) ed
// è nell'ordine di lettura del modello cartaceo.
// -----------------------------------------------------------------------------
export const CONTRIBUENTE_ORDIN_FIELDS = arrayOfAll()([
    'codice_fiscale',
    'cognome_denom',
    'nome',
    'data_nascita',
    'sesso',
    'comune_nascita',
    'provincia_nascita',
    'comune_dom_fisc',
    'provincia_dom_fisc',
    'indirizzo_dom_fisc',
    'cod_fiscale_coobbl',
    'cod_identif_coobbl',
]);
/** Nel Semplificato il domicilio fiscale è un blocco a sé, non del contribuente. */
export const CONTRIBUENTE_SEMPL_FIELDS = arrayOfAll()([
    'codice_fiscale',
    'cognome_denom',
    'nome',
    'data_nascita',
    'sesso',
    'comune_nascita',
    'provincia_nascita',
    'cod_fiscale_coobbl',
    'cod_identif_coobbl',
]);
export const DOMICILIO_FISCALE_FIELDS = arrayOfAll()(['comune', 'provincia', 'indirizzo']);
export const VERSANTE_FIRMATARIO_FIELDS = arrayOfAll()([
    'tipo',
    'codice_fiscale',
    'cognome',
    'nome',
    'data_nascita',
    'sesso',
    'comune_nascita',
    'provincia_nascita',
]);
/** Quadratura di sezione: presente su tutte le sezioni dell'Ordinario. */
export const SECTION_TOTAL_FIELDS = arrayOfAll()(['totale_debito', 'totale_credito', 'saldo', 'segno_saldo']);
export const AMOUNT_FIELDS = arrayOfAll()([
    'importo_debito',
    'importo_credito',
]);
export const ROW_ERARIO_FIELDS = arrayOfAll()([
    'codice_tributo',
    'rateazione',
    'anno_riferimento',
    ...AMOUNT_FIELDS,
]);
export const ROW_INPS_FIELDS = arrayOfAll()([
    'codice_sede',
    'causale',
    'matricola',
    'periodo_da',
    'periodo_a',
    ...AMOUNT_FIELDS,
]);
export const ROW_REGIONI_FIELDS = arrayOfAll()([
    'codice_regione',
    'codice_tributo',
    'rateazione',
    'anno_riferimento',
    ...AMOUNT_FIELDS,
]);
export const ROW_IMU_EL_FIELDS = arrayOfAll()([
    'codice_ente',
    'flag_ravvedimento',
    'flag_immob_variati',
    'flag_acconto',
    'flag_saldo',
    'num_immobili',
    'rateazione',
    'anno_riferimento',
    'codice_tributo',
    ...AMOUNT_FIELDS,
]);
export const ROW_INAIL_FIELDS = arrayOfAll()([
    'codice_sede',
    'codice_ditta',
    'cod_controllo',
    'num_riferimento',
    'causale',
    ...AMOUNT_FIELDS,
]);
export const ROW_ALTRI_EP_FIELDS = arrayOfAll()([
    'codice_sede',
    'causale',
    'posizione',
    'periodo_da',
    'periodo_a',
    ...AMOUNT_FIELDS,
]);
export const ROW_SEMPL_FIELDS = arrayOfAll()([
    'id_sezione',
    'codice_ente',
    'codice_tributo',
    'flag_ravvedimento',
    'flag_immob_variati',
    'flag_acconto',
    'flag_saldo',
    'num_immobili',
    'rateazione',
    'anno_riferimento',
    'detrazione',
    ...AMOUNT_FIELDS,
]);
export const ROW_ELIDE_FIELDS = arrayOfAll()([
    'tipo',
    'el_identificativi',
    'codice_tributo',
    'anno_riferimento',
    'importo_debito',
]);
//# sourceMappingURL=f24-wire-types.js.map